import { useQuery } from "@tanstack/react-query";
import { Mail } from "lucide-react";
import { Badge, DataTable, EmptyState, formatRelative, Notice, PageHeader, Panel } from "@hostdock/ui";
import { api } from "../api";
import { RequirePermission } from "../lib/guard";

/** A value the node could not measure is shown as "Unavailable", never as zero or healthy. */
const Flag = ({ v }: { v: boolean | null }) => (v === null ? <Badge>Unavailable</Badge> : v ? <Badge tone="success" dot>Running</Badge> : <Badge tone="danger" dot>Stopped</Badge>);

export function MailHealthPage() {
  return <RequirePermission anyOf={["MONITORING_VIEW"]}><MailHealth /></RequirePermission>;
}

function MailHealth() {
  const q = useQuery({ queryKey: ["admin-mail-health"], queryFn: () => api.mail.health(), refetchInterval: 30_000 });
  return (
    <>
      <PageHeader title="Email" description="Mail server health of nodes that run the mail stack. Nodes without it are not listed." />
      <Notice tone="info" title="What this measures">
        Each row is a live check made by the node agent just now: whether the mail container runs, which services inside it are up, and how many messages are waiting in the delivery queue.
        Delivery success to other providers, spam scores and blocklist status are not measured here.
      </Notice>
      <Panel flush>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(h) => h.serverId}
          empty={<EmptyState icon={Mail} title="No node runs the mail stack" description="Install it with infrastructure/ansible/mail.yml (see docs/email.md)." />}
          columns={[
            { key: "n", header: "Node", cell: (h) => <div><p className="font-medium">{h.server}</p>{h.error && <p className="text-[12px] text-danger">{h.error}</p>}</div> },
            { key: "r", header: "Container", cell: (h) => <Flag v={h.running} /> },
            { key: "p", header: "Postfix", cell: (h) => <Flag v={h.services.postfix} />, hideOnMobile: true },
            { key: "d", header: "Dovecot", cell: (h) => <Flag v={h.services.dovecot} />, hideOnMobile: true },
            { key: "s", header: "Rspamd", cell: (h) => <Flag v={h.services.rspamd} />, hideOnMobile: true },
            { key: "q", header: "Queue", cell: (h) => (h.queueSize === null ? <span className="text-muted">Unavailable</span> : <span className={h.queueSize > 100 ? "font-medium text-warning" : ""}>{h.queueSize}</span>) },
            { key: "dm", header: "Domains", cell: (h) => h.domains },
            { key: "c", header: "Checked", cell: (h) => <span className="text-muted">{formatRelative(h.checkedAt)}</span>, hideOnMobile: true },
          ]}
        />
      </Panel>
    </>
  );
}
